import { useEffect, useState } from 'react';
import { IconDownload, IconShare2, IconX } from '@tabler/icons-react';

/** Minimal local type — `beforeinstallprompt` isn't in the standard lib DOM. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'admitly.installPromptDismissed';

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari exposes navigator.standalone instead of display-mode.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/**
 * Unobtrusive, dismissible "Install app" affordance.
 *  - Android/Chrome: captures `beforeinstallprompt`, shows a small banner whose
 *    button triggers the native install dialog.
 *  - iOS Safari (no beforeinstallprompt): a one-time hint to use Share → Add to
 *    Home Screen.
 * Dismissal persists in localStorage and the banner never shows in standalone.
 */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;

    // Android / Chromium path.
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS path: no event to capture, so offer the manual hint.
    if (isIos()) {
      setIosHint(true);
      setShow(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  }

  async function install() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice.catch(() => undefined);
    // Whatever the choice, don't nag again.
    dismiss();
    setDeferred(null);
  }

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="surface flex w-full max-w-md items-center gap-3 px-4 py-3 shadow-pop">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy">
          <IconDownload size={18} className="text-cream" />
        </div>
        <div className="min-w-0 flex-1">
          {iosHint ? (
            <p className="text-sm text-ink-soft">
              Install Admitly: tap <IconShare2 size={13} className="inline -mt-0.5 text-navy" />{' '}
              <span className="font-medium text-navy">Share</span> →{' '}
              <span className="font-medium text-navy">Add to Home Screen</span>.
            </p>
          ) : (
            <p className="text-sm text-ink-soft">
              <span className="font-medium text-ink">Install Admitly</span> for quick access from your
              home screen.
            </p>
          )}
        </div>
        {!iosHint && (
          <button
            type="button"
            onClick={install}
            className="shrink-0 rounded-lg bg-navy px-3 py-2 text-sm font-medium text-cream hover:bg-navy-soft transition"
          >
            Install
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="shrink-0 flex h-9 w-9 items-center justify-center rounded-lg text-muted hover:bg-cream-deep/60 transition"
        >
          <IconX size={18} />
        </button>
      </div>
    </div>
  );
}
